/**
 * RecentCampaignCard — whole card navigates to campaign workflow (no duplicate CTA).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Lightbulb, TrendingUp, Trash2, CalendarDays, Building2 } from 'lucide-react';

interface CampaignCardProps {
  id: string;
  name: string;
  clientName: string;
  status:
    | 'draft'
    | 'active'
    | 'paused'
    | 'completed'
    | 'archived'
    | 'scheduled'
    | 'running'
    | 'exported'
    | 'cancelled';
  createdAt: string;
  templateType?: 'email' | 'landing_page' | 'social' | 'content';
  onDelete?: (id: string) => void;
}

const getStatusStyles = (status: string): string => {
  switch (status) {
    case 'active':
    case 'scheduled':
    case 'running':
      return 'bg-emerald-100 text-emerald-900 border-emerald-200';
    case 'draft':
      return 'bg-amber-50 text-amber-900 border-amber-200';
    case 'paused':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'completed':
    case 'exported':
      return 'bg-slate-100 text-slate-800 border-slate-200';
    case 'archived':
    case 'cancelled':
      return 'bg-slate-50 text-slate-600 border-slate-200';
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200';
  }
};

/** Backend `scheduled` = setup done / workflow can continue (not a calendar launch time). */
const getStatusLabel = (status: string): string => {
  const s = (status || 'draft').toLowerCase();
  if (s === 'scheduled' || s === 'running' || s === 'active') return 'In progress';
  if (s === 'exported') return 'Exported';
  if (s === 'cancelled') return 'Cancelled';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const getStatusTitle = (status: string): string => {
  const s = (status || 'draft').toLowerCase();
  if (s === 'scheduled') {
    return 'Workflow in progress — stored as “scheduled” after setup (not a future send date).';
  }
  return `Status: ${getStatusLabel(status)}`;
};

const getTemplateIcon = (type?: string) => {
  switch (type) {
    case 'email':
      return <Zap className="h-4 w-4 text-emerald-600" />;
    case 'social':
      return <TrendingUp className="h-4 w-4 text-emerald-600" />;
    case 'landing_page':
    case 'content':
      return <Lightbulb className="h-4 w-4 text-emerald-600" />;
    default:
      return null;
  }
};

export const RecentCampaignCard: React.FC<CampaignCardProps> = ({
  id,
  name,
  clientName,
  status,
  createdAt,
  templateType,
  onDelete,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const workflowHref = `/campaign/${encodeURIComponent(id)}/workflow/1`;

  return (
    <article className="group relative rounded-lg bg-white shadow-sm ring-1 ring-emerald-900/[0.14] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:ring-emerald-500/50 active:translate-y-0 active:shadow-md">
      {onDelete && !confirmDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setConfirmDelete(true);
          }}
          className="absolute right-2 top-10 z-10 rounded p-1.5 text-slate-300 transition-all duration-150 hover:scale-110 hover:bg-slate-100 hover:text-red-500 active:scale-95"
          title="Delete campaign"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      {onDelete && confirmDelete && (
        <div
          className="absolute right-2 top-10 z-10 flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1 shadow-sm"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <span className="text-xs text-slate-500">Delete?</span>
          <button
            type="button"
            onClick={() => {
              onDelete(id);
              setConfirmDelete(false);
            }}
            className="rounded px-1.5 py-0.5 text-xs font-semibold text-red-600 transition-colors duration-150 hover:bg-red-50 hover:text-red-800"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="rounded px-1.5 py-0.5 text-xs text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-700"
          >
            No
          </button>
        </div>
      )}

      <Link
        to={workflowHref}
        aria-label={`Open campaign ${name}`}
        className="block cursor-pointer rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
      >
        <div
          className="h-1 w-full shrink-0 rounded-t-lg bg-gradient-to-r from-emerald-500 to-teal-500 transition-[height,filter] duration-200 ease-out group-hover:h-1.5 group-hover:brightness-110"
          aria-hidden
        />
        <div className="p-3 pt-3 pr-10">
          <div className="mb-2 flex items-start justify-between gap-2">
            <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900 group-hover:text-emerald-900">
              {name}
            </h3>
            <span
              className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusStyles(
                status
              )}`}
            >
              {getStatusLabel(status)}
            </span>
          </div>

          <div className="mb-2 space-y-1">
            <p className="flex items-center gap-1.5 text-xs text-slate-600">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-emerald-600/80" />
              <span className="font-medium text-slate-700">Client</span>
              <span className="truncate">{clientName}</span>
            </p>
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              Created {formattedDate}
            </p>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-100 pt-2 sm:flex-row sm:items-center sm:justify-between">
            {templateType ? (
              <div className="flex min-w-0 items-center gap-1.5 text-slate-600">
                {getTemplateIcon(templateType)}
                <span className="truncate text-xs capitalize text-slate-600">
                  {templateType.replace('_', ' ')}
                </span>
              </div>
            ) : (
              <span className="text-xs text-slate-400">Campaign</span>
            )}
            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-slate-500 transition-all duration-200 ease-out group-hover:text-emerald-600 sm:text-right">
              Open
              <span className="inline-block transition-transform duration-200 ease-out group-hover:translate-x-0.5">→</span>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default RecentCampaignCard;
